import { Card } from "../../../components/Card";
import { BackButton } from "../../../components/Button/BackButton";
import { AchievementSection } from "./AchievementSection";
import { AchievementFullViewSkeleton, AchievementSectionsSkeleton } from "./AchievementFullViewSkeleton";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../../../services/user-context";
import { api as badgeCenterApi } from "../../../services/apis/badge-center";
import { ACHIEVEMENT_STATUS } from "../utils/constant";

function FilterButton({ selected, onClick, text }: { selected: boolean, onClick: () => void, text: string }) {
    return (
        <button className={`flex items-center rounded-[5px] gap-2 text-black text-base border border-[#B7B7B7] ${selected ? "bg-[#E4E4E7]" : "bg-white hover:opacity-70"} px-4 py-1`} onClick={onClick}>
            <span className="text-black text-base">{text}</span>
        </button>
    )
}

type AchievementFilter = { value: string; label: string };

function getEmptyMessage(selectedFilter: string, storedFilters: AchievementFilter[]) {
    if (selectedFilter === "all") {
        return "No Achievements Found";
    }

    const matchedFilter = storedFilters.find((filter) => filter.value === selectedFilter);

    if (selectedFilter === "not_started" || matchedFilter?.label === "Not Started") {
        return "No achievements to start yet";
    }

    return matchedFilter ? `No Achievements ${matchedFilter.label}` : "No Achievements Found";
}

export function AchievementFullView({ onCloseAchievementFullView }: { onCloseAchievementFullView: () => void }) {
    const [selectedFilter, setSelectedFilter] = useState("all");
    const [storedFilters, setStoredFilters] = useState<AchievementFilter[]>([]);
    const { userInfo } = useUser() as any;
    const { data: achievements, isLoading: isLoadingAchievements } = useQuery({
        queryKey: ["achievements", selectedFilter],
        queryFn: () => badgeCenterApi.getAchievements(selectedFilter),
        enabled: !!userInfo?.id,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (achievements?.filters?.length) {
            setStoredFilters(achievements.filters);
        }
    }, [achievements?.filters]);

    const filters = achievements?.filters?.length ? achievements.filters : storedFilters;
    const categories = achievements?.categories || [];
    const emptyMessage = getEmptyMessage(selectedFilter, filters);
    const isInitialLoad = !userInfo?.id || (isLoadingAchievements && filters.length === 0);

    if (isInitialLoad) {
        return <AchievementFullViewSkeleton onCloseAchievementFullView={onCloseAchievementFullView} />;
    }

    return (
        <Card className="!p-6">
            <div className="flex justify-center relative">
                <div className="absolute -top-1 left-0">
                    <BackButton text="Back" onClick={onCloseAchievementFullView} />
                </div>
                <div className="flex flex-col gap-1 items-center">
                    <span className="text-black text-2xl font-bold">Achievements</span>
                    <span>Build your skills and earn badges</span>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <FilterButton selected={selectedFilter === "all"} onClick={() => setSelectedFilter("all")} text="All" />
                {filters.map((filter) => (
                    <FilterButton key={filter.value} selected={selectedFilter === filter.value} onClick={() => setSelectedFilter(filter.value)} text={filter.label} />
                ))}
            </div>
            {isLoadingAchievements ? (
                <AchievementSectionsSkeleton />
            ) : categories.length === 0 ? (
                <div className="mt-6">
                    <span className="text-black text-base">{emptyMessage}</span>
                </div>
            ) : (
                <div className="flex flex-col gap-4 mt-6">
                    {categories.map((category, index) => (
                        <div key={category.key} className="flex flex-col gap-4">
                            <AchievementSection sectionName={category.name} totalCollected={category.achievements?.filter(a => a.status === ACHIEVEMENT_STATUS.EARNED).length} total={category.achievements?.length} badges={category.achievements} emptyMessage={emptyMessage} />
                            {index < categories.length - 1 && <div className="h-px bg-lightGray w-full" />}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    )
}